/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { ThemeType } from "@packages/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  spacing,
  Text,
  useIsDesktopViewport,
} from "@packages/ui";
import type { ReactNode } from "react";

import { StripeInfoDialog } from "../StripeInfoDialog";

type GivingInstruction = {
  title: string;
  action?: ReactNode;
  instructions: string[];
};

const instructionsContainer: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
};
const instructionStepContainer: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};
const instructionTitleContainer: Interpolation = {
  alignItems: "center",
  display: "inline-flex",
  gap: spacing.xxs,
};
const instructionTitle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHighest,
  textAlign: "left",
});
const instructionsList: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedLow,
  listStyleType: "disc",
  paddingLeft: spacing.m,
});

export const GivingCampaignInstructions = () => {
  const t = useTranslations();
  const isDesktopViewport = useIsDesktopViewport();

  const GIVING_CAMPAIGN_INSTRUCTIONS: GivingInstruction[] = [
    {
      title: t("giving_select_campaign_details"),
      instructions: [
        t("giving_instructions_choose_a_challenge"),
        t("giving_instructions_provide_description"),
        t("giving_instructions_set_campaign_start_date_and_goal"),
      ],
    },
    {
      title: t("giving_set_up_stripe_account"),
      action: <StripeInfoDialog />,
      instructions: [
        t("giving_instructions_provide_parish_bank_credentials"),
        t("giving_instructions_identity_verification"),
      ],
    },
    {
      title: t("giving_ready_to_launch"),
      instructions: [t("giving_instructions_payouts_occur_monthly")],
    },
  ];

  if (isDesktopViewport) {
    return (
      <div css={instructionsContainer}>
        {GIVING_CAMPAIGN_INSTRUCTIONS.map(
          ({ title, action, instructions }, index) => (
            <div
              key={`giving-setup-step-${index}`}
              css={instructionStepContainer}
            >
              <span css={instructionTitleContainer}>
                <Text type="title" size="m" css={instructionTitle}>
                  {`${index + 1}. ${title}`}
                </Text>
                {action}
              </span>
              <ul css={instructionsList}>
                {instructions.map((instruction, instructionIndex) => (
                  <Text
                    type="body"
                    size="m"
                    key={`giving-instruction-${index}-${instructionIndex}`}
                    asChild
                  >
                    <li>{instruction}</li>
                  </Text>
                ))}
              </ul>
            </div>
          ),
        )}
      </div>
    );
  }

  return (
    <Accordion css={instructionsContainer} type="multiple">
      {GIVING_CAMPAIGN_INSTRUCTIONS.map(
        ({ title, action, instructions }, index) => (
          <AccordionItem
            key={`giving-setup-step-${index}`}
            value={`giving-setup-step-${index}`}
            css={instructionStepContainer}
          >
            <AccordionTrigger>
              <span css={instructionTitleContainer}>
                <Text
                  type="title"
                  size="m"
                  css={instructionTitle}
                >{`${index + 1}. ${title}`}</Text>
                {action}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <ul css={instructionsList}>
                {instructions.map((instruction, instructionIndex) => (
                  <Text
                    type="body"
                    size="m"
                    key={`giving-instruction-${index}-${instructionIndex}`}
                    asChild
                  >
                    <li>{instruction}</li>
                  </Text>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ),
      )}
    </Accordion>
  );
};

export default GivingCampaignInstructions;
